import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TimerIcon,
  TeamIcon,
  NotificationIcon,
  TeamSettingsIcon,
  LocationIcon,
  TwoFactorIcon,
  ScheduleIcon,
  InsiderThreatsIcon,
  HelpIcon,
  ScreenshotIcon,
  SettingsIcon,
  SupportIcon,
  WebcamIcon,
  VacationIcon,
  EmailIcon,
} from './Icon';
import { sendRequest } from '../api/shootAPI';
import API from '../api/endpoints';

// Feature mapping - maps feature names to icons
const featureIconMap = {
  'Desktop Time Tracking App': TimerIcon,
  'Team Engagement': TeamIcon,
  'Scheduled Email': NotificationIcon,
  Members: TeamSettingsIcon,
  'Geo Dashboard': LocationIcon,
  'Two-factor Authentication': TwoFactorIcon,
  Timesheets: ScheduleIcon,
  'Insider Threats': InsiderThreatsIcon,
  'Help Center': HelpIcon,
  'Screenshot Frequency': ScreenshotIcon,
  'Settings & Permissions': SettingsIcon,
  'Chat Support': SupportIcon,
  'Webcam Audit': WebcamIcon,
  'Time Off Requests': VacationIcon,
  'Email Support SLA (24x5)': EmailIcon,
  'Single sign-on': SettingsIcon,
};

const StyledFeature = styled('div')`
  max-width: 276px;
  width: 100%;
  font-size: 14px;
  border-radius: 8px;
  box-shadow: 0px 4px 30px 0px rgba(40, 65, 183, 0.10);
`;

function SubscriptionDetails({ workspaceId, showUpgradePrompt = false, fallbackContent = null }) {
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingSubscriptions, setUpcomingSubscriptions] = useState([]);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        setLoading(true);
        const response = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.features(workspaceId),
        });

        if (response.success) {
          setSubscriptionDetails(response.data);
        }

        // Fetch upcoming subscriptions
        const upcomingResponse = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.getUpcomingSubscriptions(workspaceId),
        });

        if (upcomingResponse.data?.upcomingSubscriptions) {
          setUpcomingSubscriptions(upcomingResponse.data.upcomingSubscriptions);
        }
      } catch (error) {
        console.error('Error fetching subscription details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (workspaceId) {
      fetchSubscriptionDetails();
    }
  }, [workspaceId]);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, my: 2,
      }}
      >
        <CircularProgress size={20} />
        <span>Loading subscription details...</span>
      </Box>
    );
  }

  if (!subscriptionDetails || !subscriptionDetails.hasActiveSubscription) {
    return (
      <Box sx={{ my: 2 }}>
        <Typography variant="body1" sx={{ mb: 2, color: '#CC4525' }}>
          <strong>No active subscription found</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          {'You don\'t have an active subscription. Please purchase a plan to access premium features.'}
        </Typography>
        {fallbackContent}
      </Box>
    );
  }

  // Get enabled features with icons
  const enabledFeatures = subscriptionDetails.features
    ? Object.entries(subscriptionDetails.features)
      .filter(([feature, value]) => {
        // Exclude Members and Data Retention from features list as they'll be shown separately
        if (feature === 'Members' || feature === 'Data Retention') return false;
        return value === true || (typeof value === 'string' && value !== 'NA' && value !== '');
      })
      .map(([feature, value]) => ({
        name: feature,
        value,
        icon: featureIconMap[feature],
      }))
    : [];

  // Extract key subscription details
  const membersAllowed = subscriptionDetails.features?.Members;
  const dataRetentionDays = subscriptionDetails.features?.['Data Retention'];

  return (
    <>
      <Typography variant="h6" sx={{ mb: 1, fontSize: '16px' }}>
        Your workspace is on the
        {' '}
        <strong>
          {subscriptionDetails.plan?.name || 'Unknown'}
          {' '}
          Plan
        </strong>
      </Typography>

      {subscriptionDetails.subscriptionExpiry && (
        <Typography
          variant="body2"
          sx={{ mb: 1, fontSize: '14px', color: '#666' }}
        >
          {new Date(subscriptionDetails.subscriptionExpiry) > new Date() ? (
            <>
              Subscription expires on
              {' '}
              <strong>
                {new Date(subscriptionDetails.subscriptionExpiry).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </strong>
            </>
          ) : (
            <span style={{ color: '#CC4525' }}>
              <strong>Subscription expired</strong>
            </span>
          )}
        </Typography>
      )}

      {/* Display key subscription metrics */}
      {(membersAllowed || dataRetentionDays) && (
        <Box sx={{
          mt: 2,
          mb: 2,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
        }}
        >
          {membersAllowed && (
            <Box sx={{
              padding: '12px 16px',
              backgroundColor: '#F0F7FF',
              borderRadius: '8px',
              border: '1px solid #4062E533',
              flex: '1 1 auto',
              minWidth: '200px',
            }}
            >
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>
                Users Allowed
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 600, color: '#213D69' }}>
                {membersAllowed}
              </Typography>
            </Box>
          )}
          {dataRetentionDays && (
            <Box sx={{
              padding: '12px 16px',
              backgroundColor: '#F0F7FF',
              borderRadius: '8px',
              border: '1px solid #4062E533',
              flex: '1 1 auto',
              minWidth: '200px',
            }}
            >
              <Typography variant="body2" sx={{ fontSize: '12px', color: '#666', mb: 0.5 }}>
                Data Retention
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 600, color: '#213D69' }}>
                {dataRetentionDays}
                {' '}
                days
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {enabledFeatures.length > 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Your plan includes:
          </Typography>
          <div className="payment-features d-flex flex-wrap gap-2">
            {enabledFeatures.map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <StyledFeature key={i} className="d-flex align-items-center p-2 feature">
                  {IconComponent && <IconComponent />}
                  <span className="pl-2">
                    {feature.name}
                    {typeof feature.value === 'string' && feature.value !== 'Included' && (
                      <span style={{ color: '#666', marginLeft: '4px', fontSize: '12px' }}>
                        (
                        {feature.value}
                        )
                      </span>
                    )}
                    {typeof feature.value === 'number' && (
                      <span style={{ color: '#666', marginLeft: '4px', fontSize: '12px' }}>
                        (
                        {feature.value}
                        {' '}
                        days)
                      </span>
                    )}
                  </span>
                </StyledFeature>
              );
            })}
          </div>
        </Box>
      )}

      {upcomingSubscriptions.length > 0 && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: '16px', color: '#2196F3' }}>
            📅 Upcoming Subscription
            {upcomingSubscriptions.length > 1 ? 's' : ''}
          </Typography>
          {upcomingSubscriptions.map((upcoming, index) => {
            // Get enabled features for the upcoming subscription
            const upcomingFeatures = Object.entries(upcoming)
              .filter(([key, value]) => {
                // Exclude metadata fields and Members/Data Retention (shown separately)
                const excludedFields = [
                  'id', 'workspaceId', 'membershipId', 'startDate', 'expiryDate',
                  'isActive', 'status', 'createdAt', 'updatedAt', 'membership',
                  'Members', 'Data Retention',
                ];
                if (excludedFields.includes(key)) return false;
                return value === true || (typeof value === 'string' && value !== 'NA' && value !== '');
              })
              .map(([feature, value]) => ({
                name: feature,
                value,
                icon: featureIconMap[feature],
              }));

            return (
              <Box
                key={index}
                sx={{
                  padding: '16px',
                  backgroundColor: '#E3F2FD',
                  borderRadius: '8px',
                  border: '1px solid #2196F3',
                  mb: 2,
                }}
              >
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: '#1976D2' }}>
                  {upcoming.membership?.name || 'Unknown Plan'}
                </Typography>

                <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', mb: 0.5 }}>
                  Starts on
                  {' '}
                  <strong>
                    {new Date(upcoming.startDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </strong>
                </Typography>

                <Typography variant="body2" sx={{ fontSize: '14px', color: '#666', mb: 2 }}>
                  Expires on
                  {' '}
                  <strong>
                    {new Date(upcoming.expiryDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </strong>
                </Typography>

                {/* Display key metrics */}
                <Box sx={{
                  display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2,
                }}
                >
                  {upcoming.Members && (
                    <Box sx={{
                      padding: '8px 12px',
                      backgroundColor: '#BBDEFB',
                      borderRadius: '6px',
                      border: '1px solid #2196F3',
                      flex: '1 1 auto',
                      minWidth: '120px',
                    }}
                    >
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#0D47A1', mb: 0.5 }}>
                        Users Allowed
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, color: '#1565C0' }}>
                        {upcoming.Members}
                      </Typography>
                    </Box>
                  )}
                  {upcoming['Data Retention'] && (
                    <Box sx={{
                      padding: '8px 12px',
                      backgroundColor: '#BBDEFB',
                      borderRadius: '6px',
                      border: '1px solid #2196F3',
                      flex: '1 1 auto',
                      minWidth: '120px',
                    }}
                    >
                      <Typography variant="body2" sx={{ fontSize: '11px', color: '#0D47A1', mb: 0.5 }}>
                        Data Retention
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600, color: '#1565C0' }}>
                        {upcoming['Data Retention']}
                        {' '}
                        days
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Display features */}
                {upcomingFeatures.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1, fontWeight: 500, color: '#1565C0', fontSize: '13px',
                      }}
                    >
                      Plan includes:
                    </Typography>
                    <div className="payment-features d-flex flex-wrap gap-2">
                      {upcomingFeatures.map((feature, i) => {
                        const IconComponent = feature.icon;
                        return (
                          <StyledFeature
                            key={i}
                            className="d-flex align-items-center p-2 feature"
                            style={{ backgroundColor: '#E3F2FD', border: '1px solid #90CAF9' }}
                          >
                            {IconComponent && <IconComponent />}
                            <span className="pl-2" style={{ fontSize: '13px' }}>
                              {feature.name}
                              {typeof feature.value === 'string' && feature.value !== 'Included' && (
                                <span style={{ color: '#666', marginLeft: '4px', fontSize: '11px' }}>
                                  (
                                  {feature.value}
                                  )
                                </span>
                              )}
                              {typeof feature.value === 'number' && (
                                <span style={{ color: '#666', marginLeft: '4px', fontSize: '11px' }}>
                                  (
                                  {feature.value}
                                  {' '}
                                  days)
                                </span>
                              )}
                            </span>
                          </StyledFeature>
                        );
                      })}
                    </div>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {showUpgradePrompt && subscriptionDetails.message && (
        <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
          {subscriptionDetails.message}
        </Typography>
      )}
    </>
  );
}

SubscriptionDetails.propTypes = {
  workspaceId: PropTypes.number.isRequired,
  showUpgradePrompt: PropTypes.bool,
  fallbackContent: PropTypes.node,
};

export default SubscriptionDetails;
